import { parseToRgb } from 'polished';
import { RgbColor } from 'polished/lib/types/color';

const LUMINANCE_WHITE = 1.0;
const LUMINANCE_BLACK = 0.0;
const WCAG_AA_CONTRAST_RATIO = 4.5;
const WCAG_CONTRAST_OFFSET = 0.05;
const COLOR_CHANNEL_MAX_VALUE = 255;
const RGB_LINEAR_BREAKPOINT = 0.03928;
const RGB_LINEAR_COEFFICIENT = 12.92;
const RGB_GAMMA_OFFSET_A = 0.055;
const RGB_GAMMA_OFFSET_B = 1.055;
const RGB_GAMMA_EXPONENT = 2.4;
const LUMINANCE_WEIGHT_RED = 0.2126;
const LUMINANCE_WEIGHT_GREEN = 0.7152;
const LUMINANCE_WEIGHT_BLUE = 0.0722;

/**
 * Converts an sRGB color component (0-255) to a linear R'G'B' component (0.0 - 1.0) according to the WCAG standard.
 */
const toLinearComponent = (rgbColorComponent: number) => {
  const normalizedColorComponent = rgbColorComponent / COLOR_CHANNEL_MAX_VALUE;

  if (normalizedColorComponent <= RGB_LINEAR_BREAKPOINT) {
    return normalizedColorComponent / RGB_LINEAR_COEFFICIENT;
  }

  return Math.pow((normalizedColorComponent + RGB_GAMMA_OFFSET_A) / RGB_GAMMA_OFFSET_B, RGB_GAMMA_EXPONENT);
};

/**
 * Calculates the Relative Luminance for a given RGB color using the WCAG formula.
 * L = 0.2126 * R' + 0.7152 * G' + 0.0722 * B'
 * Luminance is a value from 0.0 (black) to 1.0 (white).
 */
const getRelativeLuminance = (rgb: RgbColor) => {
  const rLinear = toLinearComponent(rgb.red);
  const gLinear = toLinearComponent(rgb.green);
  const bLinear = toLinearComponent(rgb.blue);

  return rLinear * LUMINANCE_WEIGHT_RED + gLinear * LUMINANCE_WEIGHT_GREEN + bLinear * LUMINANCE_WEIGHT_BLUE;
};

/**
 * Calculates the contrast ratio between two luminance values using the WCAG formula.
 * R = (L1 + 0.05) / (L2 + 0.05)
 */
const calculateContrastRatio = (luminance1: number, luminance2: number): number => {
  return (luminance1 + WCAG_CONTRAST_OFFSET) / (luminance2 + WCAG_CONTRAST_OFFSET);
};

export const isBackgroundLight = (color: string) => {
  const rgb = parseToRgb(color);

  if (!rgb) {
    return false;
  }

  const luminanceBackground = getRelativeLuminance(rgb);

  const contrastWithWhite = calculateContrastRatio(LUMINANCE_WHITE, luminanceBackground);
  const contrastWithBlack = calculateContrastRatio(luminanceBackground, LUMINANCE_BLACK);

  // Strict priority for light text:
  // If white meets the minimum WCAG AA threshold (4.5), we always choose it (return false),
  // even if black text mathematically provides a better contrast ratio.
  // see ticket ND-8121 for details.
  if (contrastWithWhite >= WCAG_AA_CONTRAST_RATIO) {
    return false;
  }

  return contrastWithBlack > contrastWithWhite;
};
